import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { FolderTabs } from '@/common/FolderTabs';
import { InfoSectionContainer } from '@/common/InfoSection';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { useAdminTabs } from '@/groups/coordinator/useAdminTabs';
import { DeletePhotoTab } from '@/photo/DeletePhotoTab';
import { UploadPhotoTab } from '@/photo/UploadPhotoTab';
import { PageContent } from '@/settings/settingsStyles';
import type { HelpPlacement } from '~/hooks/useHelpPlacements';
import { GroupHeaderWrapper } from '~/styles/layoutElements';
import { formatString } from '~/utils/stringFormat';

/**
 * The help placement per group type, as legacy anchors it. Follow-up, social
 * and untyped groups get a plain title.
 */
const HELP_PLACEMENT_BY_GROUP_TYPE: Partial<
  Record<components['schemas']['GroupType'], HelpPlacement>
> = {
  STUDY_HELPER_STUDY_GROUP: 'study_helper_group_settings',
  CONGREGATION: 'group_settings',
  CUSTOM_STUDY_GROUP: 'group_settings',
};

/**
 * The server's upload cap in MB, for the too-large message. The unit is its own
 * catalogue string.
 */
const MAX_UPLOAD_MB = 8;

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/group-avatar',
)({
  component: GroupAvatarScreen,
});

function GroupAvatarScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const adminTabs = useAdminTabs();
  const [activeTab, setActiveTab] = useState<'upload' | 'delete'>('upload');
  const [status, setStatus] = useState<string | null>(null);

  const { data: currentGroup } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const { mutateAsync: uploadAvatar } = api.uploadGroupAvatar.useMutation();
  const { mutateAsync: deleteAvatar } = api.deleteGroupAvatar.useMutation();

  const groupId = currentGroup?.id;
  // `has_avatar` and not `Boolean(avatar_url)`: the server falls back to the
  // configured default group avatar, so `avatar_url` is never empty.
  const hasPhoto = Boolean(currentGroup?.has_avatar);

  const refreshGroup = async (): Promise<void> => {
    // The Delete tab's visibility and the sidebar's photo both read the group.
    await Promise.all([
      api.getGroupBySlug.invalidateQueries(),
      api.listMyGroups.invalidateQueries(),
    ]);
  };

  const handleUpload = async (blob: Blob): Promise<void> => {
    if (groupId === undefined) throw new Error('Group not loaded');
    await uploadAvatar({ path: { group_id: groupId }, body: { file: blob } });
    await refreshGroup();
    setStatus(t('groups.group-photo-uploaded'));
  };

  const handleDelete = async (): Promise<void> => {
    if (groupId === undefined) throw new Error('Group not loaded');
    await deleteAvatar({ path: { group_id: groupId } });
    // Set both before the refresh, which flips `hasPhoto` false and unmounts
    // the Delete pane; the message lives outside the panes for the same reason.
    // Legacy then leaves an empty pane, where this shows Upload.
    setStatus(t('groups.group-photo-deleted'));
    setActiveTab('upload');
    await refreshGroup();
  };

  /**
   * Only `too_large` gets its own message. The other avatar error codes can't
   * be provoked from this screen: the picker rejects files over 5,120,000
   * bytes, the input takes only images, and the cropper re-encodes to a PNG of
   * at most 1024px. They fall through to the generic failure.
   */
  const uploadErrorMessage = (failure: unknown): string | undefined =>
    errorCode(failure) === 'group_avatar_too_large'
      ? formatString(
          t('groups.photo-too-big'),
          `${MAX_UPLOAD_MB} ${t('wbs.mb')}`,
        )
      : undefined;

  const deleteErrorMessage = (): string =>
    t('groups.group-photo-delete-failed');

  const photoTabs: { id: 'upload' | 'delete'; label: string }[] = [
    { id: 'upload', label: t('wbs.upload') },
    ...(hasPhoto
      ? [{ id: 'delete' as const, label: t('actions.delete') }]
      : []),
  ];

  const handleTabChange = (tab: 'upload' | 'delete') => {
    // A confirmation belongs to the action that produced it, not to the tab the
    // user moves on to.
    setStatus(null);
    setActiveTab(tab);
  };

  const groupType = currentGroup?.group_type;
  const helpPlacement = groupType
    ? HELP_PLACEMENT_BY_GROUP_TYPE[groupType]
    : undefined;

  return (
    <PageContent>
      <TabsBar tabs={adminTabs} base="/groups/$groupName/admin" />
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('wbs.photo')} placement={helpPlacement} />
      </GroupHeaderWrapper>
      <InfoSectionContainer>
        {status && <AlertBox variant="success">{status}</AlertBox>}
        <FolderTabs
          tabs={photoTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <UploadWrapper>
          {activeTab === 'upload' && (
            <UploadPhotoTab
              // The "300px, and use the delete tab" warning shows only when the
              // group already has a photo, as in legacy; otherwise it would
              // point at a Delete tab that isn't there. Unlike legacy,
              // switching tabs or opening the cropper doesn't dismiss it.
              showNotice={hasPhoto}
              onUpload={handleUpload}
              resolveErrorMessage={uploadErrorMessage}
            />
          )}
          {activeTab === 'delete' && hasPhoto && (
            <DeletePhotoTab
              infoMessage={t('groups.delete-group-photo-info')}
              buttonLabel={t('groups.delete-group-profile-photo')}
              onDelete={handleDelete}
              resolveErrorMessage={deleteErrorMessage}
            />
          )}
        </UploadWrapper>
      </InfoSectionContainer>
    </PageContent>
  );
}

const UploadWrapper = styled.div`
  margin-top: 20px;
`;
